/**
 * Cookie / analytics consent state.
 *
 * Single source of truth for whether the user has opted in to non-essential
 * tracking (Sentry telemetry, product analytics). CH/EU compliance: nothing
 * non-essential fires until status === 'granted'.
 *
 * Persisted in localStorage so the choice survives reload and (in the
 * Capacitor WebView) survives app restart.
 */

const STORAGE_KEY = 'circleup_consent'

export type ConsentStatus = 'granted' | 'denied' | 'undecided'

type ConsentListener = (status: ConsentStatus) => void
const listeners = new Set<ConsentListener>()

function readRaw(): ConsentStatus {
  if (typeof window === 'undefined') return 'undecided'
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    if (v === 'granted' || v === 'denied') return v
    return 'undecided'
  } catch {
    return 'undecided'
  }
}

export function getConsent(): ConsentStatus {
  return readRaw()
}

export function hasConsent(): boolean {
  return readRaw() === 'granted'
}

export function setConsent(status: 'granted' | 'denied'): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, status)
  } catch {
    /* private mode or storage full — fall through */
  }
  listeners.forEach((fn) => fn(status))
}

export function onConsentChange(fn: ConsentListener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
